import React, {useState, useEffect} from 'react'
import Plot from 'react-plotly.js'
import {Select} from 'antd'
import axios from 'axios'
import {useSelectedWeek} from '../../../../../../contexts/SelectedWeekContext'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {toAbsoluteUrl} from '../../../../../../../_metronic/helpers'

interface DataItem {
  week: string
  value: number
  max: number
  min: number
  geography: string
}
interface Trace {
  x: string[]
  y: number[]
  type: string
  mode: string
  marker?: {color: string}
  line?: {color: string}
  yaxis?: string
  name: string
}
interface DataTrend {
  week: string
  trend: number
}
const {Option} = Select

const StreamgraphChart = () => {
  const [datat, setDatat] = useState<DataTrend[]>([])
  const [data, setData] = useState<DataItem[]>([])
  const [selectedAgeClass, setSelectedAgeClass] = useState<string[]>(['all'])
  const [ageClass, setAgeClass] = useState<string[]>([
    '0_4',
    '5_14',
    '15_34',
    '35_59',
    '60_79',
    '80_plus',
  ])
  const [RegionClass, setRegionClass] = useState([])
  const [selectedRegionClass, setSelectedRegionClass] = useState<string[]>(['DE'])
  const {selectedWeek, setSelectedWeek} = useSelectedWeek('2023-W40')
  const [trend, setTrend] = useState('')
  const API_URL = process.env.REACT_APP_API_URL
  const [plotData, setPlotData] = useState<Trace[]>([])
  const parseAgeClasses = (selectedAgeClass: string[]): string => {
    return selectedAgeClass.join(',')
  }

  function addMissingWeek(data: DataItem[]): DataItem[] {
    const startWeek = '2023-W34' // First week in the data
    const endWeek = '2024-W18' // Last week in the data
    const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
    const missingData: DataItem[] = []
    const newData: DataItem[] = []

    let currentWeek = startWeek

    while (currentWeek !== endWeek) {
      // Move to the next week
      const [year, weekNum] = currentWeek.split('-W')
      const weekNumber = parseInt(weekNum)
      if (weekNumber < 52) {
        currentWeek = `${year}-W${(weekNumber + 1).toString()}`
      } else {
        currentWeek = `${parseInt(year) + 1}-W1`
      }
      if (!existingWeeks.has(currentWeek)) {
        newData.push({
          week: currentWeek,
          value: 0,
          max: 0,
          min: 0,
          geography: '',
        })
      }
    }

    // Find the index to insert the new week
    let insertionIndex = 0
    for (let i = 0; i < data.length; i++) {
      if (data[i].week <= '2023-W52') {
        insertionIndex = i + 1
      } else {
        break
      }
    }
    // Merge existing data with the newly added missing weeks
    const mergedData = [
      ...data.slice(0, insertionIndex),
      ...newData,
      ...data.slice(insertionIndex),
    ].sort((a, b) => {
      const aYear = parseInt(a.week.substring(0, 4), 10)
      const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
      const bYear = parseInt(b.week.substring(0, 4), 10)
      const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
      if (aYear !== bYear) {
        return aYear - bYear
      } else {
        return aWeek - bWeek
      }
    })

    return mergedData
  }
  const fetchData = async (ageClasses = selectedAgeClass, geo = selectedRegionClass) => {
    try {
      const traces: Trace[] = []
      await Promise.all(
        ageClasses.map(async (ageClass, index) => {
          // Pass index to get color from colors array
          let filterAge = parseAgeClasses([ageClass])
          if (ageClass === 'all') {
            filterAge = parseAgeClasses(['0_4', '5_14', '15_34', '35_59', '60_79', '80_plus'])
          }
          //const filterAge = parseAgeClasses(age)
          const response = await axios.get(
            API_URL + `/covid19Deaths/list?ages=${filterAge}&geography=${geo}`
          )
          let readableAge
          let selectedColor
          switch (ageClass) {
            case '0_4':
              readableAge = 'less than 5 years '
              selectedColor = '#FF2F00'
              break
            case '5_14':
              readableAge = '5 to 14 years'
              selectedColor = '#33FF57'
              break
            case '15_34':
              readableAge = '15 to 34 years'
              selectedColor = '#5733FF'
              break
            case '35_59':
              readableAge = '35 to 59 years'
              selectedColor = '#FF33DD'
              break
            case '60_79':
              readableAge = '60 to 79 years'
              selectedColor = '#1E778D'
              break
            case '80_plus':
              readableAge = '80 and older'
              selectedColor = '#4B322C'
              break
            default:
              readableAge = 'All ages'
              selectedColor = '#203879'
              break
          }
          const trace: Trace = {
            x: addMissingWeek(response.data)
              // .filter((entry) => {
              //   const currentYear = parseInt(entry.week.substring(0, 4), 10)
              //   const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
              //   return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
              // })
              .map((entry) => entry.week),
            y: addMissingWeek(response.data)
              // .filter((entry) => {
              //   const currentYear = parseInt(entry.week.substring(0, 4), 10)
              //   const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
              //   return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
              // })
              .map((entry) => entry.value),
            yaxis: 'y',
            type: 'scatter',
            mode: 'lines',
            line: {color: selectedColor}, // Assign color based on index
            name: `Deaths of patients with a diagnosis of Covid-19 - ${readableAge}`, // Add age class to trace name
          }

          traces.push(trace)
        })
      )

      const response = await axios.get(API_URL + `/covid19Deaths/list?geography=${geo}`, )
      const trace2 = {
        x: addMissingWeek(response.data).map((entry) => entry.week),
        y: addMissingWeek(response.data).map((entry) => entry.min),
        type: 'scatter',
        mode: 'lines',
        fill: 'tonexty',
        //yaxis: 'y2',
        marker: {color: '#DBDFE9'},
        name: `Statistical range of normal percentage*`,
      }
      const trace3 = {
        x: addMissingWeek(response.data).map((entry) => entry.week),
        y: addMissingWeek(response.data).map((entry) => entry.max),
        type: 'scatter',
        mode: '',
        //yaxis: 'y2',
        marker: {color: '#DBDFE9'},
        name: `max All ages`,
        showlegend: false,
      }
      if (ageClasses.includes('all') && ageClasses.length <= 1) {
        traces.unshift(trace2)
        traces.unshift(trace3)
      }

      const startWeek = '2023-W34' // First week in the data
      const endWeek2 = '2024-W18' // Last week in the data
      const existingWeeks = new Set(response.data.map((item) => item.week)) // Set to store existing weeks
      const missingData: DataItem[] = []

      let currentWeek = startWeek

      while (currentWeek !== endWeek2) {
        // Move to the next week
        const [year, weekNum] = currentWeek.split('-W')
        const weekNumber = parseInt(weekNum)
        if (weekNumber < 52) {
          currentWeek = `${year}-W${(weekNumber + 1).toString()}`
        } else {
          currentWeek = `${parseInt(year) + 1}-W1`
        }
        if (!existingWeeks.has(currentWeek)) {
          missingData.push({
            week: currentWeek,
            value: 0,
            min: 0,
            max: 0,
            geography: '',
          })
        }
      }
      const missdata: Trace = {
        x: missingData
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
          })
          .map((entry) => entry.week),
        y: missingData
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
          })
          .map((entry) => entry.value),
        yaxis: 'y',
        type: 'scatter',
        mode: 'markers',
        marker: {color: '#000000'}, // Assign color based on index
        name: `Missing Data `, // Add age class to trace name
      }
      traces.push(missdata)
      setPlotData(traces)
    } catch (error) {
      console.error('Error fetching data:', error)
      setData([])
    }
  }
  useEffect(() => {
    const listRegionClass = async () => {
      try {
        const response = await axios.get(API_URL + '/covid19Deaths/listGeography')
        const data = response.data
        setRegionClass(data)
      } catch (error) {
        console.error('Error fetching data:', error)
        return null
      }
    }
    const trends = async (ageClasses = selectedAgeClass) => {
      try {
        ageClasses.map(async (ageClass, index) => {
          // Pass index to get color from colors array
          let filterAge = parseAgeClasses([ageClass])
          if (ageClass === 'all') {
            filterAge = parseAgeClasses(['0_4', '5_14', '15_34', '35_59', '60_79', '80_plus'])
          }
          const response = await axios.get(
            API_URL +
              `/covid19Deaths/trend?ages=${filterAge}&geography=${selectedRegionClass}&week=${selectedWeek}`
          )
          setDatat(response.data)
        })
      } catch (error) {
        console.error('Error fetching data:', error)
        return null
      }
    }
    listRegionClass()
    setSelectedAgeClass(selectedAgeClass)
    trends(selectedAgeClass)
    fetchData(selectedAgeClass, selectedRegionClass)
  }, [selectedAgeClass, selectedRegionClass, selectedWeek])

  // const trace4 = {
  //   x: data.map((entry) => entry.week),
  //   y: data.map((entry) => entry.value),
  //   yaxis: 'y2',
  //   type: 'scatter',
  //   mode: 'lines',
  //   line: { color: '#203879' },
  //   name: '#Visits for respiratory syndromes',
  // }

  // const plotData = [trace4, trace3 , trace2]
  const selectedYear = parseInt(selectedWeek.slice(0, 4))
  const selectedWeekNumber = parseInt(selectedWeek.slice(6))
  const startDate = 2023
  const startWeek = 35
  const endYear = 2024
  const endWeek = 18
  const showArrow =
    (selectedYear > startDate || (selectedYear === startDate && selectedWeekNumber >= startWeek)) &&
    (selectedYear < endYear || (selectedYear === endYear && selectedWeekNumber <= endWeek))
  const layout = {
    title: 'Deaths in patients with a diagnosis of Covid-19',
    barmode: 'stack',
    bargap: 0.2,
    bargroupgap: 0.1,
    autosize: true,
    annotations: [
      {
        xref: 'paper',
        yref: 'paper',
        x: -0.035,
        xanchor: 'right',
        y: 0.5,
        yanchor: 'bottom',
        text: '#',
        font: {
          size: 25,
          //color: '#BBBBBC',
        },
        showarrow: false,
      },
      {
        x: selectedWeek,
        y: 0,
        xref: 'x',
        yref: 'y',
        text: showArrow ? 'Selected Week' : '',
        font: {
          family: 'Courier New, monospace',
          size: 16,
          color: '#CC1B1B',
        },
        arrowcolor: '#CC1B1B',
        showarrow: showArrow,
        arrowhead: 7,
        ax: 0,
        ay: -163,
      },
      // {
      //   xref: 'paper',
      //   yref: 'paper',
      //   x: 1.02,
      //   xanchor: 'left',
      //   y: 0.5,
      //   yanchor: 'bottom',
      //   text: 'min-max',
      //   font: {
      //     size: 10,
      //   },
      //   showarrow: false,
      // },
    ],
    xaxis: {
      title: 'Weeks',
      autorange: true,
      type: 'category',
      tickangle: -45,
    },
    yaxis: {
      autorange: true,
    },
    yaxis2: {
      showgrid: false,
      titlefont: {color: 'rgb(148, 103, 189)'},
      tickfont: {color: 'rgb(148, 103, 189)'},
      overlaying: 'y',
      side: 'right',
    },
    legend: {
      x: 0,
      y: -0.5,
      orientation: 'h',
    },
  }

  function handleDataChangeAge(value): void {
    if (value.length === 0) {
      setSelectedAgeClass(['all'])
    } else {
      setSelectedAgeClass(value)
    }
  }

  function handleDataChangeRegion(value): void {
    //   if (value.includes('all')) {
    //     setSelectedRegionClass([
    //       "DE",
    // "DE.BW",
    // "DE.BY",
    // "DE.BE",
    // "DE.BB",
    // "DE.HB",
    // "DE.HH",
    // "DE.HE",
    // "DE.MV",
    // "DE.NI",
    // "DE.NW",
    // "DE.RP",
    // "DE.SL",
    // "DE.SN",
    // "DE.ST",
    // "DE.SH",
    // "DE.TH"
    //     ])
    //        fetchData([
    //         "DE",
    //         "DE.BW",
    //         "DE.BY",
    //         "DE.BE",
    //         "DE.BB",
    //         "DE.HB",
    //         "DE.HH",
    //         "DE.HE",
    //         "DE.MV",
    //         "DE.NI",
    //         "DE.NW",
    //         "DE.RP",
    //         "DE.SL",
    //         "DE.SN",
    //         "DE.ST",
    //         "DE.SH",
    //         "DE.TH"
    //        ])
    //   } else {
    setSelectedRegionClass(value)

    // }
  }
  return (
    <>
      <div className='card-body'>
        <h2 className='text-primary bold'>
          {' '}
          Deaths of patients with a diagnosis of Covid-19 (Germany){' '}
          <OverlayTrigger
            placement='right'
            overlay={
              <Tooltip id='logo-tooltip' style={{width: '300px'}}>
                <p>
                  Deaths in relation to Covid-19 are reported to the local public health department
                  in accordance with the Infection Protection Act and, from there, transmitted to
                  the Robert Koch Institute (RKI). Data is currently reported weekly.
                </p>
              </Tooltip>
            }
          >
            <img
              style={{width: '12px', marginLeft: '10px'}}
              alt='logo'
              src={toAbsoluteUrl('/media/logos/ii.svg')}
            />
          </OverlayTrigger>
        </h2>
      </div>
      <div className='row'>
        <label style={{width: '10%'}} className='col-1 mt-3 text-primary fw-medium fs-5'>
          Age group :
        </label>
        <div className='col-6'>
          <Select
            variant='borderless'
            className='border border-gray-500 border-3 rounded'
            style={{width: '71%'}}
            size='large'
            value={selectedAgeClass}
            onChange={(value) => handleDataChangeAge(value)}
            mode='multiple'
          >
            {ageClass.map((category) => {
              let readableAge
              switch (category) {
                case '0_4':
                  readableAge = 'less than 5 years'
                  break
                case '5_14':
                  readableAge = '5 to 14 years'
                  break
                case '15_34':
                  readableAge = '15 to 34 years'
                  break
                case '35_59':
                  readableAge = '35 to 59 years'
                  break
                case '60_79':
                  readableAge = '60 to 79 years'
                  break
                case '80_plus':
                  readableAge = '80 and older'
                  break
                default:
                  readableAge = 'Unknown'
                  break
              }
              return (
                <Option key={category} value={category}>
                  {readableAge}
                </Option>
              )
            })}
            <Option key={'all'} value={'all'}>
              {'All ages'}
            </Option>
          </Select>
        </div>
        <div className='col-2'></div>
        {/* {datat.map((item) => (
          <div className='col-2'>
            {item.trend !== null && (
              <div style={{textAlign: 'right'}}>
                <div className='text-primary fw-medium fs-6'>
                  Trend (past 5 weeks) :{' '}
                  <div
                    className='border border-gray-500 border-3 rounded'
                    style={{
                      display: 'inline-block', // Assure que le div n'occupe que l'espace nécessaire
                      padding: '6px', // Espace à l'intérieur du cadre
                    }}
                  >
                    <i
                      style={{
                        color:
                          item.trend === null
                            ? 'transparent'
                            : Number(item.trend) > 45
                            ? 'red'
                            : item.trend > 0
                            ? 'orange'
                            : 'green',
                        fontSize: '20px',
                        transform: `rotate(${-1 * item.trend}deg)`,
                      }}
                      className='fa-solid fa-arrow-right'
                    ></i>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))} */}
      </div>
      <div className='row'>
        <label style={{width: '10%'}} className='col-1 mt-3 text-primary fw-medium fs-5'>
          Region :
        </label>
        <div className='col-6'>
          <Select
            variant='borderless'
            className='border border-gray-500 border-3 rounded'
            style={{width: '71%'}}
            size='large'
            value={selectedRegionClass}
            onChange={(value) => handleDataChangeRegion(value)}
            //mode='multiple'
          >
            {RegionClass.map((category) => {
              let readableAge
              switch (category) {
                case 'DE':
                  readableAge = 'All regions'
                  break
                case 'DE.BW':
                  readableAge = 'Baden-Württemberg'
                  break
                case 'DE.BY':
                  readableAge = 'Bayern'
                  break
                case 'DE.BE':
                  readableAge = 'Berlin'
                  break
                case 'DE.BB':
                  readableAge = 'Brandenburg'
                  break
                case 'DE.HB':
                  readableAge = 'Bremen'
                  break
                case 'DE.HH':
                  readableAge = 'Hamburg'
                  break
                case 'DE.HE':
                  readableAge = 'Hessen'
                  break
                case 'DE.MV':
                  readableAge = 'Mecklenburg-Vorpommern'
                  break
                case 'DE.NI':
                  readableAge = 'Niedersachsen'
                  break
                case 'DE.NW':
                  readableAge = 'Nordrhein-Westfalen'
                  break
                case 'DE.RP':
                  readableAge = 'Rheinland-Pfalz'
                  break
                case 'DE.SL':
                  readableAge = 'Saarland'
                  break
                case 'DE.SN':
                  readableAge = 'Sachsen'
                  break
                case 'DE.ST':
                  readableAge = 'Sachsen-Anhalt'
                  break
                case 'DE.SH':
                  readableAge = 'Schleswig-Holstein'
                  break
                case 'DE.TH':
                  readableAge = 'Thüringen'
                  break
                default:
                  readableAge = 'Unknown'
                  break
              }
              return (
                <Option key={category} value={category}>
                  {readableAge}
                </Option>
              )
            })}
            {/* <Option key={'all'} value={'all'}>
              {'all regions'}
            </Option> */}
          </Select>
        </div>
      </div>
      <Plot
        style={{width: '100%', height: '400px'}}
        data={plotData}
        layout={layout}
        config={{
          displaylogo: false,
          responsive: true,
        }}
      />
    </>
  )
}

export default StreamgraphChart
